import React, { createContext, useContext, ReactNode } from 'react';

interface ApiContextType {
  apiUrl: string;
  makeRequest: (endpoint: string, options?: RequestInit) => Promise<Response>;
  getBrands: (filters?: any) => Promise<any>;
  syncBrands: () => Promise<any>;
  syncMetafields: () => Promise<any>;
  updateBrandDelay: (brandId: string, delayData: any) => Promise<any>;
  bulkUpdateBrands: (updates: any[]) => Promise<any>;
  getSettings: () => Promise<any>;
  updateSettings: (settings: any) => Promise<any>;
}

const ApiContext = createContext<ApiContextType | undefined>(undefined);

export const useApi = () => {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error('useApi must be used within an ApiProvider');
  }
  return context;
};

interface ApiProviderProps {
  children: ReactNode;
}

export const ApiProvider: React.FC<ApiProviderProps> = ({ children }) => {
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  const makeRequest = async (endpoint: string, options: RequestInit = {}) => {
    const url = `${apiUrl}${endpoint}`;
    
    const defaultOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, defaultOptions);
      return response;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  };

  const getBrands = async (filters?: any) => {
    let endpoint = '/api/brands';
    if (filters) {
      const params = new URLSearchParams(filters);
      endpoint += `?${params.toString()}`;
    }
    const response = await makeRequest(endpoint);
    return response.json();
  };

  const syncBrands = async () => {
    const response = await makeRequest('/api/brands/sync', { method: 'POST' });
    return response.json();
  };

  const syncMetafields = async () => {
    const response = await makeRequest('/api/metafields/sync', { method: 'POST' });
    return response.json();
  };

  const updateBrandDelay = async (brandId: string, delayData: any) => {
    const response = await makeRequest(`/api/brands/${brandId}/delay`, {
      method: 'PUT',
      body: JSON.stringify(delayData),
    });
    return response.json();
  };

  const bulkUpdateBrands = async (updates: any[]) => {
    const response = await makeRequest('/api/brands/bulk-update', {
      method: 'PUT',
      body: JSON.stringify({ updates }),
    });
    return response.json();
  };

  const getSettings = async () => {
    const response = await makeRequest('/api/settings');
    return response.json();
  };

  const updateSettings = async (settings: any) => {
    const response = await makeRequest('/api/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
    return response.json();
  };

  return (
    <ApiContext.Provider value={{ 
      apiUrl, 
      makeRequest, 
      getBrands, 
      syncBrands, 
      syncMetafields, 
      updateBrandDelay, 
      bulkUpdateBrands, 
      getSettings, 
      updateSettings 
    }}>
      {children}
    </ApiContext.Provider>
  );
};
